import React, { useCallback, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import Grid from '@mui/material/Grid'
import { ReactComponent as UploadIcon } from '../../assets/images/upload-bottom.svg'
import DescriptionIcon from '@mui/icons-material/Description'
import { Alert } from '../../shared/components/Alert'
import Box from '@mui/material/Box'

const Dropzone = ({ generate, setFile, acceptedFileTypes, maxFiles }) => {
    const dropzoneStyle = {
        width: '100%',
        height: '120px',
        border: '2px dashed #0049EA',
        borderRadius: '5px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        margin: '10px 0px',
        paddingRight: '10px',
    }
    const [files, setFiles] = useState([])
    const onDrop = useCallback((acceptedFiles) => {
        if (acceptedFiles?.length) {
            setFiles(acceptedFiles)
        }
    }, [])

    const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
        onDrop,
        multiple: true,
        accept: acceptedFileTypes,
        ...(maxFiles ? { maxFiles } : {}),
    })

    useEffect(() => {
        if (files?.length) {
            generate(files)
            return () => files.forEach((file) => URL.revokeObjectURL(file.preview))
        }
    }, [files])

    const removeFile = (name) => {
        setFiles((files) => files.filter((file) => file.name !== name))
        setFile({})
    }

    // error messages only for DropZone, Server response errors are displayed in parent component
    const fileRejectionItems = (() => {
        // flatten error messages, deduplicate with Set to only display once
        const allErrors = fileRejections.flatMap(({ file, errors }) => errors.map((e) => ({ code: e.code, file })))
        const uniqueCodes = [...new Set(allErrors.map((e) => e.code))]

        return uniqueCodes.map((code, idx) => {
            // default catch-all message
            let errorMessage = 'Could not process file.'
            switch (code) {
                case 'file-invalid-type':
                    const allowedExtensions = Object.values(acceptedFileTypes).flat().join(', ')
                    errorMessage = `Unsupported file type. Supported types: ${allowedExtensions}`
                    break
                case 'too-many-files':
                    errorMessage = maxFiles
                        ? `Too many files. Maximum is ${maxFiles}.`
                        : `Too many files. Please drop a folder or select multiple PDFs.`
                    break
                default:
                    break
            }

            return (
                <Box sx={{ width: '100%', marginTop: '10px' }} key={idx}>
                    <Alert severity="error" message={errorMessage} />
                </Box>
            )
        })
    })()

    return (
        <form>
            {files.length === 0 && (
                <a href="javascript:void(0)" style={{ color: 'inherit' }}>
                    <Grid item>{fileRejections.length > 0 && fileRejectionItems}</Grid>
                    <Grid
                        container
                        {...getRootProps({
                            style: dropzoneStyle,
                        })}
                        sx={{ backgroundColor: 'background.paper' }}
                    >
                        <input {...getInputProps()} />
                        <Grid container alignItems="center" justifyContent="center" direction="column">
                            <Grid item>
                                <UploadIcon
                                    style={{ height: '37px', width: '37px', color: '#0049EA', marginTop: '16px' }}
                                />
                            </Grid>
                            {isDragActive ? (
                                <Grid item>
                                    <p>
                                        {maxFiles
                                            ? `Drop up a File or a Folder Here...`
                                            : 'Drop Files or Folder Here...'}
                                    </p>
                                </Grid>
                            ) : (
                                <Grid item>
                                    <p>
                                        {maxFiles
                                            ? `Drop up a File or a Folder here to Upload`
                                            : 'Drop Files / Folder or Click To Upload'}
                                    </p>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </a>
            )}

            {/* Preview */}
            <Grid container xs={12} direction="column" alignItems="center" justifyContent="center">
                {files.length === 1 && (
                    <Grid item border="2px blue solid" padding={2} borderRadius={'6px'}>
                        <button type="button" onClick={() => removeFile(files[0].name)}>
                            del
                        </button>
                        <p>{files[0].name}</p>
                        <DescriptionIcon color="primary" />
                    </Grid>
                )}
                {files.length > 1 && (
                    <Grid item padding={2}>
                        <DescriptionIcon color="primary" />
                        <p>{files.length} files selected</p>
                    </Grid>
                )}
            </Grid>
        </form>
    )
}

export default Dropzone
